<template>
    <div>
        <p
            class="text-overline grey--text text--darken-1 mb-0"
            style="line-height: 1.5;"
        >
            Bulk Message Send
        </p>
        <h1 class="text-h6 font-weight-medium">
            {{ bulk_message.label }}
        </h1>

        <v-card
            class="shaded-light px-4 py-3 mt-3 mb-8"
            style="border-left: 5px solid var(--v-success-base)"
            elevation="0"
        >
            <p class="d-flex align-center gap-2 mb-0">
                <v-icon
                    size="24"
                    color="success"
                    aria-hidden="true"
                >
                    mdi-check-circle
                </v-icon>
                <span>
                    This message has been sent. You can monitor the send process below.
                </span>
            </p>
        </v-card>

        <message-patterns-readonly
            :bulk_message="bulk_message"
            class="mt-4 mb-5"
        />

        <recipients-tracker
            :organization_id="organization_id"
            :bulk_message="bulk_message"
            :bulk_message_id="bulk_message_id"
            :has_sent="has_sent"
            :message_state="message_state"
            :progress_detail="progress_detail"
            @export-recipients-to-excel="$emit('export-recipients-to-excel', $event)"
            @update-progress-detail="$emit('update-progress-detail', $event)"
            @update="$emit('update')"
            @pause="$emit('pause', $event)"
            @resume="$emit('resume', $event)"
            @cancel="$emit('cancel', $event)"
        />

        <template v-if="['cancelled', 'paused', 'finished'].includes(message_state)">
            <recipients-table-send
                :bulk_message="bulk_message"
                :message_state="message_state"
                :has_sent="has_sent"
                :loading_recipients="loading_recipients"
                :selected_recipient_filter="selected_recipient_filter"
                :recipients="recipients"
                :recipient_extra_fields="recipient_extra_fields"
                :full_text="full_text"
                :num_recipients_under_current_filter="num_recipients_under_current_filter"
                :progress_detail="progress_detail"
                :table_options.sync="local_table_options"
                class="mb-10"
                @update-full-text="$emit('update-full-text', $event)"
                @update-recipient-filter="$emit('update-recipient-filter', $event)"
                @update-recipients="$emit('update-recipients', $event)"
                @change-outcome="$emit('change-outcome', $event)"
                @view-imports="$emit('view-imports')"
            />
        </template>
        <template v-else>
            <v-alert
                type="info"
                outlined
                dense
                class="mt-6 mb-10"
            >
                The recipients table will appear here once the message has finished sending.
            </v-alert>
        </template>

        <div class="d-flex align-center mt-10">
            <v-icon
                color="primary"
                aria-hidden="true"
            >
                mdi-link-variant
            </v-icon>
            <h2 class="ml-2">Links</h2>
        </div>

        <v-card
            class="shaded-light mt-2 mb-10"
            flat
        >
            <v-card-text>
                <m-list-buttons :items="links_buttons" />
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import MessagePatternsReadonly from '@/components/bulk/message-patterns-readonly.vue';
import RecipientsTracker from "@/components/bulk/recipients-tracker.vue";
import RecipientsTableSend from "@/components/bulk/recipients-table-send.vue";

export default {
    name: 'BulkMessageSendState',

    components: {
        MessagePatternsReadonly,
        RecipientsTracker,
        RecipientsTableSend,
    },

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            required: true,
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        message_state: {
            type: String,
            required: true,
        },
        message_type: {
            type: String,
            required: true,
        },
        message_type_force_mms: {
            type: Boolean,
            required: false,
            default: false
        },
        has_sent: {
            type: Boolean,
            required: true,
        },
        loading_recipients: {
            type: Boolean,
            required: false,
            default: false,
        },
        progress_detail: {
            type: Object,
            required: false
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        selected_recipient_filter: {
            type: String,
            required: false,
            default: 'ready_to_send',
        },
        recipients: {
            type: Array,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        table_options: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            links_buttons: [
                { title: 'Share', sub_title: 'Share this link with fellow RedChirp users in your account', icon: 'mdi-share-outline', action: this.onShare },
                { title: 'Order Attribution', sub_title: 'View analytics for orders attributed to this bulk message', icon: 'mdi-google-analytics', action: this.linkToOrderAttribution },
                { title: 'Change Log', sub_title: 'View account change log entries for this bulk message', icon: 'mdi-format-list-bulleted-square', action: this.linkToChangeLog },
            ],
        };
    },

    computed: {
        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            },
        },
    },

    methods: {
        onShare() {
            this.$rc.rest({
                spinner: true,
                endpoint: 'LIST: /api/v1/organizations/employees/labels',

                params: {
                    organization_id: this.organization_id,
                },

                on_success: (data) => {
                    this.pickShareRecipients(data.results);
                },

                on_error: 'Unable to get employee list',
            });
        },

        pickShareRecipients(employees) {
            const choices = employees
                .filter(e => !e.is_archived)
                .map(e => {
                    return {
                        text: e.employee_label,
                        value: e.employee_id,
                    };
                });

            this.$rc.pick({
                title: 'Users to Share With',
                content_before: null,
                content_after: 'Selected users will get a text and/or email with a link to this page',

                choices: choices,

                multiple: true,
                type: 'select',
                select_label: 'Select Users',
                initial_value: null,
                unset_label: null,

                on_ok: (value) => {
                    this.doShareWithRecipients(value);
                },

                on_cancel: () => { },
            });
        },

        doShareWithRecipients(employee_ids) {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/messages/send-status-detail/share',

                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                    employee_ids: employee_ids,
                },

                on_success: () => {
                    this.$rc.snackbar('Sharing ' + (1 == employee_ids.length ? 'message' : 'messages') + ' sent');
                },

                on_error: 'Unable to share',
            });
        },

        linkToOrderAttribution() {
            const route = {
                name: 'analytics-one-bulk-message-analytics-group',
                params: {
                    organization_id: this.organization_id,
                    analytics_group_id: this.bulk_message.comp_analytics_group_id,
                },
            };

            const resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },

        linkToChangeLog() {
            const route = {
                name: 'account-change-log',
                params: {
                    organization_id: this.organization_id,
                },
                query: {
                    subject_uuid: this.bulk_message_id,
                },
            };

            const resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.theme--light.v-list) {
    background: transparent;
    padding: 0;
}
</style>
